import axios, { AxiosRequestHeaders } from "axios";
import {
  BitrixTask,
  CalibrationVideo,
  Camera,
  Campaign,
  CreateCampaignInput,
  CvatProject,
  Execution,
  ExecutionStatus,
  Network,
  PhotoCollection,
  User,
  Video,
  VideoRecord,
  WorkflowCatalog
} from "../types";


const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  }
});

const getCookieValue = (name: string): string | null => {
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        return decodeURIComponent(cookie.substring(name.length + 1));
      }
    }
  }
  return null;
};

api.interceptors.request.use(
  (config) => {
    const headers: AxiosRequestHeaders = new axios.AxiosHeaders()
    const item = localStorage.getItem("authentication")
    if (item) {
      const authentication = JSON.parse(item)

      if (authentication.accessToken) {
        headers.Authorization = `Bearer ${authentication.accessToken}`
      }
    }

    Object.entries(headers).forEach(([key, value]) => {
      config.headers.set(key, value)
    })
    return config
  },
)


export interface BatchCreateRecordVideoInput {
  title: string;
  content_type: string;
  file_size: number;
  hash_id: string;
  camera_id: string;
  camera_model?: string;
  camera_mode?: string;
  date_time?: string;
}

export interface BatchCreateRecordInput {
  name: string;
  srid: number;
  date_time: string;
  videos: BatchCreateRecordVideoInput[];
}

export interface BatchCreatedVideo {
  video_id: number;
  hash_id: string;
  upload_url: string;
  blob_name: string;
  title: string;
  content_type: string;
}

export interface BatchCreatedRecord {
  record_id: number;
  name: string;
  videos: BatchCreatedVideo[];
}

export const RecordsAPI = {
  getByNetwork: (networkUuid: string) =>
    api.get<VideoRecord[]>(`/records/?network_uuid=${networkUuid}`),
  getByCampaign: (campaignId: number) =>
    api.get<VideoRecord[]>(`/records/?campaign=${campaignId}`),
  create: async (data: { name: string; srid: number, date_time: string; campaign: number }) => {
    //await getFreshCsrfToken();
    return api.post<VideoRecord>("/records/", data);
  },
  batchCreateRecords: async (campaignId: number, records: BatchCreateRecordInput[]) => {
    await getFreshCsrfToken();
    return api.post<{ results: BatchCreatedRecord[] }>(
      `/campaigns/${campaignId}/batch_create_records/`,
      { records }
    );
  },
  getRecord: (recordId: number) =>
    api.get<VideoRecord>(`/records/${recordId}/`),
  getExecutions: (recordId: number) =>
    api.get<Execution[]>(`/records/${recordId}/executions/`),
  getExecutionStatus: (executionId: number | undefined) => {
    if (!executionId) {
      return Promise.reject(new Error("No execution ID provided"));
    }
    return api.get<ExecutionStatus>(`/executions/${executionId}/status/`);
  },
  getExecutionDetails: (executionId: number) =>
    api.get<Execution>(`/executions/${executionId}/`),
  cancelExecution: async (executionId: number) => {
    await getFreshCsrfToken();
    return api.post<Execution>(`/executions/${executionId}/cancel/`);
  },
  terminateExecution: async (executionId: number) => {
    await getFreshCsrfToken();
    return api.post<Execution>(`/executions/${executionId}/terminate/`);
  },
  getExecutionNodeLogs: (executionId: number, nodeId: string, tailLines?: number) =>
    api.get<{ logs: string }>(`/executions/${executionId}/node_logs/`, {
      params: {
        node_id: nodeId,
        ...(tailLines !== undefined ? { tail_lines: tailLines } : {})
      }
    }),
  requestUpload: async (
    recordId: number,
    data: {
      title: string;
      content_type: string;
      type: string;
      hash_id: string;
      file_size: number; camera_id: string; camera_model: string; camera_mode: string;
    }
  ) => {
    await getFreshCsrfToken();
    return api.post<{ upload_url: string; blob_name: string }>(
      `/records/${recordId}/request_upload/`,
      data
    );
  },
  confirmUpload: async (
    recordId: number,
    data: { hash_id: string }
  ) => {
    console.log("confirmUpload", data);
    await getFreshCsrfToken();
    return api.post(`/records/${recordId}/confirm_upload/`, data);
  },
  getWorkflowCustomData: async () => {
    await getFreshCsrfToken();
    return api.get<WorkflowCatalog>(`/records/get_workflows_custom_data/`);
  },

  startWorkflowsBatch: async (items: Array<{ record_id: number }>, custom_data?: { [key: string]: unknown }) => {
    await getFreshCsrfToken();
    return api.post(`/records/start_workflows_batch/`, {
      items,
      custom_data: custom_data || {}
    });
  },
  startAnnotationsExportBatch: async (items: Array<{ record_id: number }>, custom_data?: { [key: string]: unknown }) => {
    await getFreshCsrfToken();
    return api.post(`/records/start_annotations_export_batch/`, {
      items,
      custom_data: custom_data || {}
    });
  },
  deleteExtractedProcessedFiles: async (recordIds: number[]) => {
    await getFreshCsrfToken();
    return api.post<{ results: Array<{ record_id: number; record_name: string | null; status: string; error?: string }> }>(`/records/delete_all_files_in_extracted_processed_containers/`, {
      record_ids: recordIds
    });
  },
  checkHashIds: (hashIds: string[]) =>
    api.post<{ results: Array<{ hash_id: string; exists: boolean; is_uploaded: boolean; video_id: number | null; record_id: number | null; record_name: string | null }> }>(
      `/records/check_hash_ids/`,
      { hash_ids: hashIds }
    ),
  listBlobDownloadUrls: (recordId: number, containerType: 'raw' | 'extracted' | 'processed' | 'l2r') =>
    api.get<{ download_urls: string[] }>(`/records/${recordId}/list_blob_download_urls/?container_type=${containerType}`),
  listBlobDownloadUrlsWithFolders: (recordId: number, containerType: 'raw' | 'extracted' | 'processed' | 'l2r') =>
    api.get<{ folders: { [key: string]: string[] } }>(`/records/${recordId}/list_blob_download_urls_with_folders/?container_type=${containerType}`),
  getJpegUrl: (recordId: number, containerType: 'raw' | 'extracted' | 'processed', blob_name: string) =>
    api.get<{ jpeg_url: string }>(`/records/${recordId}/get_jpeg_url/${blob_name}/?container_type=${containerType}`),
};

export const VideosAPI = {
  setMaster: async (videoId: number) => {
    await getFreshCsrfToken();
    return api.post(`/videos/${videoId}/set_master/`);
  },
  refreshUrl: (videoId: number) => {
    return api.get<Video>(`/videos/${videoId}/refresh_url/`);
  },
  requestReupload: async (videoId: number) => {
    await getFreshCsrfToken();
    return api.post<{ upload_url: string; blob_name: string }>(`/videos/${videoId}/request_reupload/`);
  },
  confirmReupload: async (videoId: number) => {
    await getFreshCsrfToken();
    return api.post<Video>(`/videos/${videoId}/confirm_reupload/`);
  },
};

export const CalibrationVideosAPI = {
  get: (videoId: number) => {
    return api.get<CalibrationVideo>(`/calibrationvideos/${videoId}/`);
  },
  refreshUrl: (videoId: number) => {
    return api.get<CalibrationVideo>(`/calibrationvideos/${videoId}/refresh_url/`);
  },
  startCalibrationOrchestrator: async (videoId: number, customData?: { [key: string]: unknown }) => {
    await getFreshCsrfToken();
    return api.post(`/calibrationvideos/${videoId}/start_calibration_orchestrator/`, {
      custom_data: customData || {}
    });
  },
  deleteVideo: async (videoId: number) => {
    await getFreshCsrfToken();
    return api.delete(`/calibrationvideos/${videoId}/`);
  }
};

/**
 * Read the current CSRF token from the csrftoken cookie and set it on the
 * default Axios headers so that the next mutating request includes it.
 * Django sets this cookie on the first GET request when CSRF middleware is
 * active.
 */
export const getFreshCsrfToken = async (): Promise<void> => {
  const csrfToken = getCookieValue("csrftoken");
  if (csrfToken) {
    api.defaults.headers.common["X-CSRFTOKEN"] = csrfToken;
  }
};

export const NetworksAPI = {
  getNetworks: async () => {
    // The Authorization header with the Bearer token will be added automatically by the request interceptor
    return api.get<Network[]>('/network/networks/');
  },
  getNetworkDetail: async (networkSlug: string) => {
    return api.get<Network>(`/network/networks/${networkSlug}/`);
  },
};

export const CamerasAPI = {
  createCamera: async (data: { unique_id: string; name: string }) => {
    await getFreshCsrfToken();
    return api.post<Camera>("/cameras/", data);
  },
  getCameras: async () => {
    return api.get<Camera[]>(`/cameras/`);
  },
  getAllCalibrationVideos: async () => {
    return api.get<CalibrationVideo[]>(`/calibrationvideos/`);
  },
  getCamera: async (cameraId: number) => {
    return api.get<Camera>(`/cameras/${cameraId}/`);
  },
  deleteCamera: async (cameraId: string) => {
    await getFreshCsrfToken();
    return api.delete(`/cameras/${cameraId}/`);
  },
  requestCalibrationVideoUpload: async (
    cameraId: string,
    data: { title: string; content_type: string }
  ) => {
    await getFreshCsrfToken();
    // Create or get existing camera (backend returns existing camera with 200 status)
    await api.post<Camera>("/cameras/", { unique_id: cameraId, name: cameraId });
    await getFreshCsrfToken();
    return api.post<{ upload_url: string; blob_name: string }>(
      `/cameras/${cameraId}/request_upload/`,
      data
    );
  },
  confirmCalibrationVideoUpload: async (
    cameraId: string,
    data: { blob_name: string; title: string; content_type: string; date_time?: string, file_size?: number, camera_model?: string, camera_mode?: string }
  ) => {
    console.log("confirmUpload", data);
    await getFreshCsrfToken();
    return api.post(`/cameras/${cameraId}/confirm_upload/`, data);
  },
  setCalibrationVideo: async (cameraId: string, videoId: number | null) => {
    await getFreshCsrfToken();
    return api.post(`/cameras/${cameraId}/set_calibration_video/`, { calibration_video_id: videoId });
  },
}
export const PhotoAPI = {
  getCollections: (networkSlug: string) => {
    return api.get<PhotoCollection[]>(`/photo/collections/`, {
      params: { network: networkSlug }
    });
  },
};


export interface CheckAndUpdateVideoResult {
  record_id: number;
  video_id: number;
  blob_name: string;
  is_uploaded: boolean;
  size: number;
  uploaded_size: number;
}

export interface RefreshedExecutionSubStatus {
  record_id: number;
  execution_id: number;
  sub_status: string | null;
}

export const CampaignAPI = {
  getAllCampaigns: () =>
    api.get<Campaign[]>(`/campaigns/`),

  getCampaigns: (filters?: {
    network_uuid?: string;
    status?: 'planned' | 'running' | 'finished';
    start_date?: string;
    end_date?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.network_uuid) params.append('network_uuid', filters.network_uuid);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const queryString = params.toString();
    return api.get<Campaign[]>(`/campaigns/${queryString ? `?${queryString}` : ''}`);
  },

  getCampaign: (campaignId: number) =>
    api.get<Campaign>(`/campaigns/${campaignId}/`),

  createCampaign: async (data: CreateCampaignInput) => {
    await getFreshCsrfToken();
    return api.post<Campaign>(`/campaigns/`, data);
  },

  updateType: async (campaignId: number, type: Campaign['type']) => {
    await getFreshCsrfToken();
    return api.patch<Campaign>(`/campaigns/${campaignId}/`, { type });
  },

  checkAndUpdateVideos: (campaignId: number) =>
    api.post<{ results: CheckAndUpdateVideoResult[] }>(`/campaigns/${campaignId}/check_and_update_videos/`),

  // Re-derives execution.sub_status for every errored execution of the campaign. Failures
  // from the last 24h that still lack one are first re-synced from Argo, the only place
  // their error code lives until then.
  refreshExecutionSubStatuses: (campaignId: number) =>
    api.post<{ results: RefreshedExecutionSubStatus[] }>(
      `/campaigns/${campaignId}/refresh_execution_sub_statuses/`
    ),
};

export const CvatProjectsAPI = {
  getCvatProjects: () => api.get<CvatProject[]>(`/cvat-projects/`),
};

export const BitrixAPI = {
  getTasks: () => api.get<BitrixTask[]>(`/bitrix/tasks/`),
};

export const AuthAPI = {
  getMe: () => api.get<User>(`/auth/me/`),
};

export const DebugAPI = {
  triggerBackendError: () => {
    // This endpoint is designed to throw an error for testing Sentry
    return api.get(`/sentry/debug/`);
  },
};

export default api;
